"use client"

import { useState, useEffect } from "react"
import { X, Delete } from "lucide-react"

type AddChargeProps = {
  onAdd: (description: string, amount: number) => void
  onCancel: () => void
  editingCharge?: { id: string; description: string; amount: number } | null
  onUpdate?: (id: string, description: string, amount: number) => void
}

export function AddCharge({ onAdd, onCancel, editingCharge, onUpdate }: AddChargeProps) {
  const [amount, setAmount] = useState(editingCharge ? editingCharge.amount.toString() : "0")
  const [description, setDescription] = useState(editingCharge ? editingCharge.description : "")

  useEffect(() => {
    if (editingCharge) {
      setAmount(editingCharge.amount.toString())
      setDescription(editingCharge.description)
    }
  }, [editingCharge])

  const handleNumberClick = (num: string) => {
    if (amount === "0" && num !== ".") {
      setAmount(num)
    } else if (num === "." && amount.includes(".")) {
      return
    } else if (amount.split(".")[1]?.length >= 2 && num !== "backspace") {
      return
    } else {
      setAmount(amount + num)
    }
  }

  const handleBackspace = () => {
    if (amount.length === 1) {
      setAmount("0")
    } else {
      setAmount(amount.slice(0, -1))
    }
  }

  const handleSubmit = () => {
    const numAmount = Number.parseFloat(amount)
    if (numAmount > 0 && description.trim()) {
      if (editingCharge && onUpdate) {
        onUpdate(editingCharge.id, description.trim(), numAmount)
      } else {
        onAdd(description.trim(), numAmount)
      }
    }
  }

  const isValid = Number.parseFloat(amount) > 0 && description.trim().length > 0

  return (
    <div className="fixed inset-0 z-50 flex min-h-screen flex-col bg-background">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4">
        <div className="w-10" />
        <h1 className="text-lg font-semibold text-gray-900">{editingCharge ? "Edit charge" : "Add charge"}</h1>
        <button onClick={onCancel} className="flex h-10 w-10 items-center justify-center text-gray-900">
          <X className="h-6 w-6" />
        </button>
      </div>

      {/* Description Input */}
      <div className="px-6 py-4">
        <input
          type="text"
          placeholder="Description (e.g., Fuel surcharge)"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          className="w-full border-0 border-b border-gray-300 bg-transparent px-0 text-base text-gray-900 placeholder:text-gray-400 focus:outline-none focus:border-primary rounded-none"
        />
      </div>

      {/* Amount Display */}
      <div className="flex flex-1 items-center justify-center py-12">
        <div className="text-7xl font-bold text-gray-900 tracking-tight">${amount}</div>
      </div>

      {/* Number Pad */}
      <div className="px-6 pb-8">
        <div className="grid grid-cols-3 gap-4 mb-6">
          {["1", "2", "3", "4", "5", "6", "7", "8", "9"].map((num) => (
            <button
              key={num}
              onClick={() => handleNumberClick(num)}
              className="flex h-16 items-center justify-center text-3xl font-bold text-gray-900 active:bg-gray-100 rounded-lg transition-colors"
            >
              {num}
            </button>
          ))}
          <button
            onClick={() => handleNumberClick(".")}
            className="flex h-16 items-center justify-center text-3xl font-bold text-gray-900 active:bg-gray-100 rounded-lg transition-colors"
          >
            .
          </button>
          <button
            onClick={() => handleNumberClick("0")}
            className="flex h-16 items-center justify-center text-3xl font-bold text-gray-900 active:bg-gray-100 rounded-lg transition-colors"
          >
            0
          </button>
          <button
            onClick={handleBackspace}
            className="flex h-16 items-center justify-center text-gray-900 active:bg-gray-100 rounded-lg transition-colors"
          >
            <Delete className="h-6 w-6" />
          </button>
        </div>

        <button
          onClick={handleSubmit}
          disabled={!isValid}
          className="w-full h-14 text-lg font-semibold bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed rounded-xl"
        >
          {editingCharge ? "Save" : "Add"}
        </button>
      </div>

      {/* Bottom Safe Area */}
      <div className="h-8 bg-background" />
    </div>
  )
}
